import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  signal,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { EmailValidationService } from '../../../services/email-validation.service';
import { LoginFlow } from '../../../types/auth.types';
import { LogoHeaderComponent } from '../shared/logo-header/logo-header.component';
import { LoginService } from '../../../services/login.service';
import {
  TipoProveedorOAuthEnum,
  TipoProveedorOauth,
} from '../../../constants/tipo-proveedor-oauth.enum';
import {
  LoginState,
  FinishState,
  ToAccessState,
  ConfirmInformationState,
  LoginInitialState,
} from '../../../types/login.types';
import { ErrorCodeEnum } from '../../../constants/error-code.enum';
import { LoadingSpinnerComponent } from '../shared/loading-spinner/loading-spinner.component';
import { UrlParamsService } from '../../../services/url-params.service';
import { JwtService } from '../../../services/jwt.service';
import { JwtPayload } from 'jwt-decode';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    LogoHeaderComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  currentState: LoginInitialState | undefined | null;

  email: string = '';

  // Error específico del email
  emailError: {
    message: string;
  } | null = null;

  // Error general
  error: {
    message: string;
  } | null = null;

  TIPO_PROVEEDOR_OAUTH = TipoProveedorOAuthEnum;
  isProcessingLogin = signal<boolean>(false);

  @Output() onLogin = new EventEmitter<LoginFlow>();
  @Output() onRegister = new EventEmitter<LoginFlow>();
  @Output() onLoggedIn = new EventEmitter<LoginFlow>();

  constructor(
    private router: Router,
    private emailValidationService: EmailValidationService,
    private loginService: LoginService,
    private urlParamsService: UrlParamsService,
    private jwtService: JwtService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.loginService.loginState$.subscribe((state) => {
      if (state.type === LoginState.login) {
        this.currentState = state as LoginInitialState;
        this.email = this.currentState?.email || '';
        this.error = this.currentState?.error
          ? { message: this.currentState?.error }
          : null;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Only process OAuth callbacks on browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Facebook Authorization Code Grant callback (via API redirect)
    const facebookProvider = this.urlParamsService.getParam('provider');
    const fbAccessToken = this.urlParamsService.getParam('access_token');
    const fbOpenId = this.urlParamsService.getParam('openId');
    const oauthError = this.urlParamsService.getParam('error');

    if (facebookProvider === 'facebook' && oauthError) {
      this.error = {
        message:
          'No se pudo iniciar sesión con Facebook. Por favor, inténtalo de nuevo.',
      };
      this.urlParamsService.clearAllParams();
      return;
    }

    if (facebookProvider === 'facebook' && fbAccessToken && fbOpenId) {
      this.isProcessingLogin.set(true);
      const email = this.urlParamsService.getParam('email') || '';
      const displayName = this.urlParamsService.getParam('displayName') || '';
      this.urlParamsService.clearAllParams();

      this.processSocialLogin({
        idProveedor: TipoProveedorOAuthEnum.FACEBOOK,
        idToken: fbOpenId,
        identificador: email,
        preferedName: displayName,
        accessToken: fbAccessToken,
      });
      return;
    }

    // Get idToken from AppleID as a URL query parameter
    const idToken = this.urlParamsService.getParam('id_token');
    if (idToken) {
      this.isProcessingLogin.set(true);
      try {
        const decoded = await this.jwtService.decodeToken<
          JwtPayload & { email: string; name?: string }
        >(idToken);

        if (decoded) {
          console.log('Apple Sign-In token decoded successfully:', decoded);

          // Validate that we have the required fields
          if (!decoded.sub || !decoded.email) {
            console.error('Apple Sign-In token missing required fields:', {
              sub: decoded.sub,
              email: decoded.email,
            });
            return;
          }

          // Clear URL parameters
          this.urlParamsService.clearAllParams();

          this.processSocialLogin({
            idProveedor: TipoProveedorOAuthEnum.APPLE,
            idToken: decoded.sub,
            identificador: decoded.email,
            preferedName: decoded.name || '', // Apple might provide name in some cases
          });
        } else {
          console.warn('Failed to decode Apple Sign-In token');
        }
      } catch (error) {
        console.error('Error processing Apple Sign-In token:', error);
        // Optionally show user-friendly error message
        this.error = {
          message:
            'Error al procesar el inicio de sesión con Apple. Por favor, inténtalo de nuevo.',
        };
      } finally {
        this.isProcessingLogin.set(false);
      }
    }
  }

  login(): void {
    this.isProcessingLogin.set(true);

    this.emailValidationService.validateEmailInUse(this.email).subscribe({
      next: (response) => {
        this.isProcessingLogin.set(false);

        if (response?.success === true && response?.data) {
          const state: ToAccessState = {
            type: LoginState.toAccess,
            timestamp: new Date(),
            loginFlow: {
              idEmail: response.data.idEmail,
              email: this.email,
              idUsuarioDigital: response.data.idUsuarioDigital,
              idTelefono: response.data.idTelefono,
              telefono: response.data.telefono,
              prefijoTelefono: response.data.prefijoTelefono,
            },
            availableMethods: ['password', 'email', 'sms'],
          };
          this.loginService.notifyLoginState(state);
        } else {
          if (response.errorCode === ErrorCodeEnum.EMAIL_NO_REGISTRADO) {
            const state: ConfirmInformationState = {
              type: LoginState.confirmInformation,
              timestamp: new Date(),
              loginFlow: {
                name: '',
                email: this.email,
                idProveedor: TipoProveedorOAuthEnum.CREDENCIAL,
              },
            };
            this.loginService.notifyLoginState(state);
          } else {
            const state: LoginInitialState = {
              type: LoginState.login,
              email: this.email,
              error: 'Ocurrió un error al validar el email',
              timestamp: new Date(),
            };
            this.loginService.notifyLoginState(state);
          }
        }
      },
      error: () => {
        const state: LoginInitialState = {
          type: LoginState.login,
          timestamp: new Date(),
          email: this.email,
          error: 'Ocurrió un error al iniciar sesión',
        };
        this.loginService.notifyLoginState(state);
      },
    });
  }

  async processSocialLogin(payload: {
    idProveedor: TipoProveedorOauth;
    idToken: string;
    identificador: string;
    preferedName: string;
    accessToken?: string;
  }) {
    this.isProcessingLogin.set(true);

    this.emailValidationService
      .socialLogin({
        idProveedor: payload.idProveedor,
        idToken: payload.idToken,
        identificador: payload.identificador,
        accessToken: payload.accessToken,
      })
      .subscribe({
        next: (responseOAuth) => {
          if (
            responseOAuth?.success === false &&
            responseOAuth?.errorCode ===
              ErrorCodeEnum.CUENTA_DIGITAL_NO_ENCONTRADA
          ) {
            const state: ConfirmInformationState = {
              type: LoginState.confirmInformation,
              timestamp: new Date(),
              loginFlow: {
                name: payload.preferedName,
                email: payload.identificador,
                idProveedor: payload.idProveedor,
                idToken: payload.idToken,
              },
            };
            this.isProcessingLogin.set(false);
            this.loginService.notifyLoginState(state);
            return;
          }

          if (responseOAuth?.success === true && responseOAuth?.data) {
            const state: FinishState = {
              type: LoginState.finish,
              timestamp: new Date(),
              user: responseOAuth?.data?.user,
              loginFlow: {
                email: payload.identificador,
                idProveedor: payload.idProveedor,
                idToken: payload.idToken,
                name: payload.preferedName,
              },
              success: true,
            };
            this.loginService.notifyLoginState(state);
          } else {
            this.isProcessingLogin.set(false);
            const state: LoginInitialState = {
              type: LoginState.login,
              email: this.email,

              error: responseOAuth?.message,
              timestamp: new Date(),
            };
            this.isProcessingLogin.set(false);
            this.loginService.notifyLoginState(state);
          }
        },
        error: () => {
          const state: LoginInitialState = {
            type: LoginState.login,
            timestamp: new Date(),
            email: this.email,
            error: 'Ocurrió un error al iniciar sesión',
          };
          this.isProcessingLogin.set(false);
          this.loginService.notifyLoginState(state);
        },
      });
  }

  async socialLogin(idProveedor: TipoProveedorOauth) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    switch (idProveedor) {
      case TipoProveedorOAuthEnum.GOOGLE:
        return this.loginGoogle();
      case TipoProveedorOAuthEnum.FACEBOOK:
        return this.loginFacebook();
      case TipoProveedorOAuthEnum.MICROSOFT:
        return this.loginMicrosoft();
      case TipoProveedorOAuthEnum.APPLE:
        return this.loginApple();
      default:
        return;
    }
  }

  async loginApple() {
    try {
      await this.loginService.loginWithApple();
    } catch (error) {
      console.log('Error al iniciar sesión con Apple', error);
    }
  }

  async loginGoogle() {
    try {
      const result = await this.loginService.loginWithGoogle();
      const user = result;

      this.processSocialLogin({
        idProveedor: TipoProveedorOAuthEnum.GOOGLE,
        idToken: user.idToken,
        identificador: user.email || '',
        preferedName: user.displayName || '',
      });
    } catch (error) {
      console.log('Error al iniciar sesión con Google', error);
    }
  }

  async loginFacebook() {
    try {
      this.loginService.loginWithFacebook();
    } catch (error) {
      console.log('Error al iniciar sesión con Facebook', error);
    }
  }

  async loginMicrosoft() {
    try {
      const result = await this.loginService.loginWithMicrosoft();
      const user = result;

      this.processSocialLogin({
        idProveedor: TipoProveedorOAuthEnum.MICROSOFT,
        idToken: user.idToken,
        identificador: user.email || '',
        preferedName: user.displayName || '',
      });
    } catch (error) {
      console.log('Error al iniciar sesión con Microsoft', error);
    }
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  continueWithEmail(): void {
    if (!this.isValidEmail(this.email)) {
      this.emailError = {
        message: 'Por favor ingresa un correo electrónico válido',
      };
      return;
    }

    this.emailError = null;
    this.login();
  }

  isValidEmail(email: string): boolean {
    /// Sacado de accounts.is_valid_email, usado por el trigger emails_tr_siempre_minuscula
    // Ademas lo usa el API para validar el email
    const emailRegex = /^[A-Za-z0-9._%-]+@[A-Za-z0-9-]+(\.[A-Za-z]{2,})+$/;
    return emailRegex.test(email);
  }
}
